import { dealPackPath, dealPackZipPath, t, type DealPackAnswer } from '@bms2/shared';
import { apiFetch, get } from './api';

/**
 * 🔒 The Deal Pack from the Deal page's More (piece 19; decision 5400; row
 * T2744). Copy for Claude reads the pack's text and puts it on the clipboard;
 * Download Deal Pack fetches the zip through the frame's own fetch, so the
 * session's headers ride along, and saves it from a blob. Each answers the
 * sentence the page says, good or refused.
 */
export type DealPackOutcome = { tone: 'good' | 'error'; text: string } | null;

export async function copyDealPack(dealId: string, apiBaseUrl: string): Promise<DealPackOutcome> {
  const read = await get<DealPackAnswer>(dealPackPath(dealId), apiBaseUrl);
  if (!read.ok) return { tone: 'error', text: `${t('deal.pack.copyFailed')} ${read.message}` };
  const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
  if (!clipboard || typeof clipboard.writeText !== 'function') return { tone: 'error', text: t('deal.pack.copyFailed') };
  try {
    await clipboard.writeText(read.data.text);
    return { tone: 'good', text: t('deal.pack.copied') };
  } catch {
    // silent: a browser refusing the clipboard is said on the page in the notice banner, and nothing is lost.
    return { tone: 'error', text: t('deal.pack.copyFailed') };
  }
}

export async function downloadDealPack(dealId: string, apiBaseUrl: string): Promise<DealPackOutcome> {
  const response = await apiFetch(`${apiBaseUrl}${dealPackZipPath(dealId)}`);
  if (!response.ok) {
    const body = (await response.json().catch(/* silent: a refusal with no JSON body still says the pack was not downloaded. */ () => null)) as { message?: unknown } | null;
    return { tone: 'error', text: [t('deal.pack.downloadFailed'), typeof body?.message === 'string' ? body.message : null].filter(Boolean).join(' ') };
  }
  // A test double answers without headers or a body; the browser's own Response always has both.
  if (typeof response.blob !== 'function' || typeof URL.createObjectURL !== 'function') return null;
  const disposition = response.headers?.get('content-disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'Deal Pack.zip';
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
  return null;
}
